/**
 * canvasTableUtils — v3 `table` 객체의 렌더·편집 공용 헬퍼 (WP-F)
 *
 * 한 곳에서 표 마크업/스타일을 정의해 세 렌더러가 같은 결과를 내게 한다.
 * - CanvasObject (편집기, React 요소)        → getTableStyle / getTableCellStyle
 * - HtmlSlideRenderer (읽기 전용 HTML 문자열) → buildTableHtml
 * - backend/services/html_slide_thumbnail.py build_table_object_html (PDF·썸네일) — 같은 규칙의 Python 포팅
 *
 * 마크업 규칙 (Python과 1:1)
 *   <div class="wqcanvas-table-wrap" style={base}>
 *     <table class="wqcanvas-table" style="width:100%;height:100%;border-collapse:collapse;table-layout:fixed;...">
 *       <colgroup><col style="width:NN%"/>…</colgroup>
 *       <tbody><tr style="height:NN%"><td data-row data-col [colspan][rowspan] style="border:…;padding:…;…">…</td></tr></tbody>
 *   - header=true → 첫 행 굵게 + style.headerFill 배경 + style.headerColor
 *   - covered 셀은 그리지 않음 (colspan/rowspan으로 덮인 자리)
 *
 * 순수 함수만 둔다 (DOM·상태 없음).
 */
import {
  DEFAULT_TABLE_BORDER,
  DEFAULT_TABLE_CELL_PADDING,
  MAX_TABLE_COLS,
  MAX_TABLE_ROWS,
  makeTableCell,
  makeTableObject,
} from '../../utils/canvasSchema';

export const TABLE_PICKER_MAX = 8;
export const TABLE_DEFAULT_FONT = 'Pretendard, system-ui, sans-serif';
export const TABLE_DEFAULT_TEXT_COLOR = '#191F28';
export const TABLE_DEFAULT_HEADER_FILL = '#F2F4F6';

/** 격자 프리셋 — 정사각 칸, semantic.role 'answer-grid' */
export const GRID_PRESETS = Object.freeze([
  { id: 'grid-5x5', label: '5×5', rows: 5, cols: 5 },
  { id: 'grid-8x8', label: '8×8', rows: 8, cols: 8 },
  { id: 'grid-10x10', label: '10×10', rows: 10, cols: 10 },
]);

export function isTableObject(obj) {
  return !!obj && obj.type === 'table';
}

export function isAnswerGridObject(obj) {
  return isTableObject(obj) && (obj.semantic?.role === 'answer-grid');
}

export function getTableDims(obj) {
  const rows = Math.max(0, Math.min(MAX_TABLE_ROWS, Math.trunc(Number(obj?.rows) || 0)));
  const cols = Math.max(0, Math.min(MAX_TABLE_COLS, Math.trunc(Number(obj?.cols) || 0)));
  return { rows, cols };
}

export function getTableCell(obj, row, col) {
  const cells = Array.isArray(obj?.cells) ? obj.cells : [];
  const rowCells = Array.isArray(cells[row]) ? cells[row] : [];
  const cell = rowCells[col];
  return cell && typeof cell === 'object' ? cell : {};
}

/** 열 너비 비율(%) — obj.colWidths(px 또는 비율)가 있으면 그 비율, 없으면 균등. Python `_table_column_widths`와 동일 */
export function getTableColumnPercents(obj) {
  const { cols } = getTableDims(obj);
  if (cols <= 0) return [];
  const raw = obj?.colWidths;
  if (Array.isArray(raw) && raw.length === cols) {
    const values = raw.map((v) => Math.max(0, Number(v) || 0));
    const total = values.reduce((sum, v) => sum + v, 0);
    if (total > 0) return values.map((v) => (v / total) * 100);
  }
  return Array.from({ length: cols }, () => 100 / cols);
}

function tableBorder(obj) {
  const border = obj?.border || {};
  const width = Number(border.width);
  return {
    color: border.color || DEFAULT_TABLE_BORDER.color,
    width: Number.isFinite(width) ? width : DEFAULT_TABLE_BORDER.width,
  };
}

function tableCellPadding(obj) {
  const pad = Number(obj?.cellPadding);
  return Number.isFinite(pad) ? pad : DEFAULT_TABLE_CELL_PADDING;
}

/** <table> 요소 스타일 (React camelCase). 호출측이 준 fontFamily(폴백·따옴표 정리본) 우선 */
export function getTableStyle(obj, fontFamily) {
  const css = obj?.style || {};
  const style = {
    width: '100%',
    height: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
    fontFamily: String(fontFamily || css.fontFamily || TABLE_DEFAULT_FONT).replace(/"/g, "'"),
    fontSize: typeof css.fontSize === 'number' ? `${css.fontSize}px` : (css.fontSize || '12px'),
    color: css.color || TABLE_DEFAULT_TEXT_COLOR,
    lineHeight: 1.3,
  };
  if (css.fontWeight) style.fontWeight = css.fontWeight;
  if (css.background) style.background = css.background;
  return style;
}

/** <td> 스타일 (React camelCase). header 행·셀 fill·정렬 규칙 포함 */
export function getTableCellStyle(obj, row, col) {
  const css = obj?.style || {};
  const cell = getTableCell(obj, row, col);
  const border = tableBorder(obj);
  const isHeader = !!obj?.header && row === 0;
  const style = {
    border: `${border.width}px solid ${border.color}`,
    padding: `${tableCellPadding(obj)}px`,
    textAlign: cell.align || css.textAlign || 'center',
    verticalAlign: css.verticalAlign || 'middle',
    overflow: 'hidden',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  };
  if (isHeader) {
    style.fontWeight = 700;
    style.background = cell.fill || css.headerFill || TABLE_DEFAULT_HEADER_FILL;
    style.color = css.headerColor || css.color || TABLE_DEFAULT_TEXT_COLOR;
  } else if (cell.fill) {
    style.background = cell.fill;
  }
  return style;
}

export function getTableRowPercent(obj) {
  const { rows } = getTableDims(obj);
  return rows > 0 ? 100 / rows : 100;
}

/** camelCase 스타일 객체 → CSS 문자열 (문자열 HTML 렌더러용) */
export function styleObjectToCss(style) {
  return Object.entries(style || {})
    .filter(([, value]) => value != null && value !== '')
    .map(([key, value]) => {
      const prop = key.replace(/[A-Z]/g, (ch) => `-${ch.toLowerCase()}`);
      const isUnitless = key === 'lineHeight' || key === 'fontWeight' || key === 'zIndex' || key === 'opacity';
      const cssValue = typeof value === 'number' && !isUnitless ? `${value}px` : String(value);
      return `${prop}:${cssValue}`;
    })
    .join(';');
}

export function escapeTableHtml(value) {
  return String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  })[c]);
}

/** 셀 내용 HTML (html 우선, 없으면 text 이스케이프) */
export function getTableCellHtml(cell) {
  if (!cell) return '';
  if (cell.html != null) return String(cell.html);
  return escapeTableHtml(cell.text || '');
}

/**
 * 표 객체 → HTML 문자열 (HtmlSlideRenderer / 백엔드 썸네일과 같은 마크업)
 * @param {object} obj table 객체
 * @param {string} baseCss 감싸는 div의 절대 좌표 스타일 문자열 (position/left/top/width/height/transform/z-index)
 * @param {string} [fontFamily]
 */
export function buildTableHtml(obj, baseCss, fontFamily) {
  const { rows, cols } = getTableDims(obj);
  if (rows <= 0 || cols <= 0) return '';
  const colHtml = getTableColumnPercents(obj)
    .map((pct) => `<col style="width:${pct.toFixed(4)}%" />`)
    .join('');
  const rowPct = getTableRowPercent(obj).toFixed(4);
  const bodyRows = [];
  for (let r = 0; r < rows; r += 1) {
    const tds = [];
    for (let c = 0; c < cols; c += 1) {
      const cell = getTableCell(obj, r, c);
      if (cell.covered) continue;
      let attrs = '';
      if (Number(cell.colspan) > 1) attrs += ` colspan="${Number(cell.colspan)}"`;
      if (Number(cell.rowspan) > 1) attrs += ` rowspan="${Number(cell.rowspan)}"`;
      tds.push(
        `<td data-row="${r}" data-col="${c}"${attrs} style="${styleObjectToCss(getTableCellStyle(obj, r, c))}">${getTableCellHtml(cell)}</td>`,
      );
    }
    bodyRows.push(`<tr style="height:${rowPct}%">${tds.join('')}</tr>`);
  }
  const tableCss = styleObjectToCss(getTableStyle(obj, fontFamily));
  return (
    `<div class="wqcanvas-table-wrap" style="${baseCss}">`
    + `<table class="wqcanvas-table" style="${tableCss}">`
    + `<colgroup>${colHtml}</colgroup><tbody>${bodyRows.join('')}</tbody></table></div>`
  );
}

/** 편집 가능한(covered 아닌) 셀인지 */
export function isEditableTableCell(obj, row, col) {
  const { rows, cols } = getTableDims(obj);
  if (row < 0 || col < 0 || row >= rows || col >= cols) return false;
  return !getTableCell(obj, row, col).covered;
}

/**
 * Tab / Shift+Tab 이동 대상 셀. 행 우선 순서로 covered 셀은 건너뛴다.
 * 표 끝(마지막 셀에서 Tab, 첫 셀에서 Shift+Tab)이면 null → 호출자가 편집을 끝낸다.
 */
export function findNextEditableCell(obj, row, col, direction = 1) {
  const { rows, cols } = getTableDims(obj);
  if (rows <= 0 || cols <= 0) return null;
  let index = row * cols + col;
  const total = rows * cols;
  for (let step = 0; step < total; step += 1) {
    index += direction;
    if (index < 0 || index >= total) return null;
    const r = Math.floor(index / cols);
    const c = index % cols;
    if (isEditableTableCell(obj, r, c)) return { row: r, col: c };
  }
  return null;
}

/** 셀 innerHTML → {text, html}. 마크업이 없으면 html을 비워 text만 저장한다 */
export function tableCellValueFromHtml(innerHtml) {
  const raw = String(innerHtml == null ? '' : innerHtml)
    .replace(/<br\s*\/?>\s*$/i, '');
  const text = raw
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
    .trim();
  const hasMarkup = /<(?!br\s*\/?>)[a-z][^>]*>/i.test(raw);
  return hasMarkup ? { text, html: raw } : { text, html: null };
}

/** 셀 하나의 내용을 바꾼 새 table 객체 (불변) */
export function setTableCellContent(obj, row, col, value) {
  const { rows, cols } = getTableDims(obj);
  if (!isEditableTableCell(obj, row, col)) return obj;
  const cells = Array.from({ length: rows }, (_, r) => Array.from({ length: cols }, (__, c) => ({
    ...getTableCell(obj, r, c),
  })));
  const next = { ...cells[row][col], text: value?.text ?? '' };
  if (value?.html) next.html = value.html;
  else delete next.html;
  cells[row][col] = next;
  return { ...obj, cells };
}

export function tableCellContentEquals(cell, value) {
  const currentText = String(cell?.text ?? '');
  const currentHtml = cell?.html ?? null;
  return currentText === String(value?.text ?? '') && (currentHtml || null) === (value?.html || null);
}

/**
 * 사용자 삽입용 기본 표 (origin 'user'). 캔버스 폭에 맞춰 열 폭 ~120px, 행 높이 ~40px.
 * PPT(1280)는 18px 글자, A4(794)는 13px 글자.
 */
export function makeUserTable(id, canvasWidth, canvasHeight, rows, cols, opts = {}) {
  const safeRows = Math.max(1, Math.min(TABLE_PICKER_MAX, Math.trunc(Number(rows) || 1)));
  const safeCols = Math.max(1, Math.min(TABLE_PICKER_MAX, Math.trunc(Number(cols) || 1)));
  const isSlide = canvasWidth >= 1000;
  const colW = Math.min(isSlide ? 160 : 120, Math.floor((canvasWidth * 0.84) / safeCols));
  const rowH = isSlide ? 48 : 36;
  const w = colW * safeCols;
  const h = rowH * safeRows;
  const x = opts.x != null ? opts.x : Math.round((canvasWidth - w) / 2);
  const y = opts.y != null ? opts.y : Math.round(Math.max(24, (canvasHeight - h) / 2));
  return makeTableObject(id, x, y, w, h, {
    rows: safeRows,
    cols: safeCols,
    header: !!opts.header,
    cellPadding: DEFAULT_TABLE_CELL_PADDING,
    border: { ...DEFAULT_TABLE_BORDER },
    style: {
      fontSize: isSlide ? 18 : 13,
      fontFamily: TABLE_DEFAULT_FONT,
      color: TABLE_DEFAULT_TEXT_COLOR,
      background: '#FFFFFF',
      headerFill: TABLE_DEFAULT_HEADER_FILL,
    },
    origin: 'user',
    z: opts.z ?? 1,
  });
}

/**
 * 사용자 삽입용 답란 격자 (origin 'user', role 'answer-grid', 정사각 칸).
 * 칸 크기는 캔버스에 들어가게 자동 (A4 8×8 → 40px, PPT 8×8 → 56px 등).
 */
export function makeUserGrid(id, canvasWidth, canvasHeight, rows, cols, opts = {}) {
  const safeRows = Math.max(1, Math.min(MAX_TABLE_ROWS, Math.trunc(Number(rows) || 1)));
  const safeCols = Math.max(1, Math.min(MAX_TABLE_COLS, Math.trunc(Number(cols) || 1)));
  const maxCell = canvasWidth >= 1000 ? 56 : 44;
  const fitW = (canvasWidth * 0.8) / safeCols;
  const fitH = (canvasHeight * 0.7) / safeRows;
  const cell = Math.max(16, Math.floor(Math.min(maxCell, fitW, fitH)));
  const w = cell * safeCols;
  const h = cell * safeRows;
  const x = opts.x != null ? opts.x : Math.round((canvasWidth - w) / 2);
  const y = opts.y != null ? opts.y : Math.round(Math.max(24, (canvasHeight - h) / 2));
  return makeTableObject(id, x, y, w, h, {
    rows: safeRows,
    cols: safeCols,
    role: 'answer-grid',
    cellPadding: 0,
    border: { ...DEFAULT_TABLE_BORDER },
    cells: Array.from({ length: safeRows }, () => Array.from({ length: safeCols }, () => makeTableCell(''))),
    style: { background: '#FFFFFF', cellSize: cell },
    semantic: { answerStyle: 'grid' },
    origin: 'user',
    z: opts.z ?? 1,
  });
}
